// PageHeader — the ONE page header (Orenza Design System v2).
//
// WHY THIS EXISTS
// 52 of 87 pages hand-rolled their own sticky header markup. That is the single
// biggest reason the app looked like different products stapled together — every
// page chose its own title size, back-link style, action layout and accent bar.
// From here, every page uses THIS. If a header needs something this doesn't offer,
// the fix is to extend this component, never to hand-roll a one-off.
//
// ANATOMY
//   ▏ signature indigo→violet rail (2px, full-bleed) — the brand gesture, on every page
//   ← back · Title                                    · [actions]
//     subtitle / meta
//
// The rail is what ties 87 pages into one product: the same brand gradient at the
// top of every screen.

import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface PageHeaderProps {
  title: ReactNode;
  /** Small line under the title — context, date range, count. */
  subtitle?: ReactNode;
  /** Back link href. Renders the ← affordance when set. */
  backHref?: string;
  backLabel?: string;
  /** Right-aligned actions — buttons, filters, segmented controls. */
  actions?: ReactNode;
  /** A meta chip shown next to the title (e.g. "304 reports", "FY26-27"). */
  badge?: ReactNode;
  /** Sticky by default; pages inside AppShell that already scroll can turn it off. */
  sticky?: boolean;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  backHref,
  backLabel = 'Back',
  actions,
  badge,
  sticky = true,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'z-20 border-b border-mist bg-frost/95 backdrop-blur print:static print:bg-transparent',
        sticky && 'sticky top-0',
        className,
      )}
    >
      {/* The signature rail. One gesture, every page. */}
      <div className="h-[3px] w-full" style={{ background: 'var(--brand-grad)' }} aria-hidden />
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 sm:px-6 sm:py-4">
        {backHref && (
          <a
            href={backHref}
            className="group inline-flex shrink-0 items-center gap-1 text-caption font-medium text-slate transition-colors hover:text-cobalt"
          >
            <span aria-hidden className="transition-transform group-hover:-translate-x-0.5">←</span>
            {backLabel}
          </a>
        )}
        {backHref && <span className="hidden h-5 w-px bg-mist sm:block" aria-hidden />}

        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h1 className="truncate font-display text-display-sm font-semibold text-obsidian sm:text-display-md">
              {title}
            </h1>
            {badge}
          </div>
          {subtitle && <p className="mt-0.5 truncate text-caption text-slate">{subtitle}</p>}
        </div>

        {actions && <div className="ml-auto flex flex-wrap items-center justify-end gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/** The standard meta chip used in `badge` — count pills, FY labels, status. */
export function HeaderBadge({ children, tone = 'brand' }: { children: ReactNode; tone?: 'brand' | 'muted' }) {
  return (
    <span
      className={cn(
        'inline-flex h-5 items-center rounded-full px-2 font-mono text-nano font-medium uppercase tracking-[0.1em] tabular-nums',
        tone === 'brand' ? 'bg-cobalt/10 text-cobalt' : 'bg-haze text-slate',
      )}
    >
      {children}
    </span>
  );
}
